import './AttachedFileItem.scss';

import React, { useMemo } from 'react';

import {
  BiDownload,
  BiSolidFile,
  BiSolidFileDoc,
  BiSolidFileJpg,
  BiSolidFilePdf,
  BiSolidFilePng,
  BiSolidFileTxt,
} from 'react-icons/bi';

/** Supported file extensions for attachment display (icons + type label). */
export const ATTACHED_FILE_SUPPORTED_EXTENSIONS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'docx',
  'pptx',
  'xlsx',
  'txt',
] as const;

export type AttachedFileExtension =
  (typeof ATTACHED_FILE_SUPPORTED_EXTENSIONS)[number];

export interface AttachedFileItemProps {
  /** Display name of the file (e.g. "document.pdf") */
  fileName: string;
  /** File size in bytes */
  fileSize: number;
  /** Optional. If provided, a remove button is shown and called when clicked. */
  onRemove?: () => void;
  /** Optional. Override extension derived from fileName (e.g. "pdf", "docx"). */
  fileExtension?: string;
  /** Optional. Extra class name for the root element. */
  className?: string;
  /** Optional. If provided, a download button is shown and called when clicked. */
  onDownload?: () => void;
}

const getExtension = (fileName: string): string => {
  const match = /\.([^.]+)$/i.exec(fileName);
  return match ? match[1].toLowerCase() : '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(bytes / 1024).toFixed(2)} KB`;
};

const getTypeLabel = (ext: string): string => {
  const labels: Record<string, string> = {
    pdf: 'PDF',
    jpg: 'JPG',
    jpeg: 'JPEG',
    png: 'PNG',
    docx: 'DOCX',
    pptx: 'PPTX',
    xlsx: 'XLSX',
    txt: 'TXT',
  };
  return labels[ext] || ext.toUpperCase();
};

const ICON_SIZE = 28;

const AttachedFileItem: React.FC<AttachedFileItemProps> = ({
  fileName,
  fileSize,
  onRemove,
  fileExtension,
  className = '',
  onDownload,
}) => {
  const ext = useMemo(
    () => (fileExtension ?? getExtension(fileName)).toLowerCase(),
    [fileName, fileExtension],
  );
  const typeLabel = useMemo(() => getTypeLabel(ext), [ext]);
  const sizeFormatted = useMemo(() => formatFileSize(fileSize), [fileSize]);

  const renderIcon = () => {
    switch (ext) {
      case 'pdf':
        return <BiSolidFilePdf size={ICON_SIZE} aria-hidden />;
      case 'jpg':
      case 'jpeg':
        return <BiSolidFileJpg size={ICON_SIZE} aria-hidden />;
      case 'png':
        return <BiSolidFilePng size={ICON_SIZE} aria-hidden />;
      case 'docx':
        return <BiSolidFileDoc size={ICON_SIZE} aria-hidden />;
      case 'pptx':
      case 'xlsx':
        return <BiSolidFile size={ICON_SIZE} aria-hidden />;
      case 'txt':
        return <BiSolidFileTxt size={ICON_SIZE} aria-hidden />;
      default:
        return <BiSolidFile size={ICON_SIZE} aria-hidden />;
    }
  };

  return (
    <div
      className={`attached-file-item ${className}`.trim()}
      data-extension={ext}>
      <span className="attached-file-item__icon" aria-hidden>
        {renderIcon()}
      </span>
      <div className="attached-file-item__info">
        <span className="attached-file-item__name" title={fileName}>
          {fileName}
        </span>
        <span className="attached-file-item__meta">
          {typeLabel} • {sizeFormatted}
        </span>
      </div>
      {onRemove != null && (
        <button
          type="button"
          className="attached-file-item__remove"
          onClick={onRemove}
          aria-label="Remove file">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden>
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      )}
      {onDownload != null && (
        <button
          type="button"
          className="attached-file-item__download"
          onClick={onDownload}
          aria-label="Download file">
          <BiDownload size={20} aria-hidden />
        </button>
      )}
    </div>
  );
};

export default AttachedFileItem;
